import { Checkbox, RadioGroup, ReadOnlyBanner, StatusPill, Tabs, UnsavedChangesGuard } from '@bb/ui';
import { useState, type ReactNode } from 'react';
import { useEnvironmentLocales } from '../shared/useEnvironmentLocales.ts';
import { issueLookup } from './pushChecks.ts';
import {
  contentFor,
  PLATFORM_LABELS,
  PLATFORMS,
  platformsOf,
  setPlatforms,
  sharedContent,
  switchMode,
  writeSeparate,
  writeShared,
  type PushComposerMode,
  type PushComposerValue,
  type PushContentInput,
  type PushPlatform,
} from './pushDraft.ts';
import { PushChecksSummary } from './PushIssues.tsx';
import { PushContentTab } from './PushContentTab.tsx';
import { DEFAULT_SURFACES, PushPreviewPane, type PreviewSurfaces } from './PushNotificationPreview.tsx';
import { PushSettingsTab } from './PushSettingsTab.tsx';
import { PushTestTab } from './PushTestTab.tsx';
import { usePushRegistry } from './usePushRegistry.ts';
import { usePushValidation } from './usePushValidation.ts';

export interface PushComposerProps {
  environmentId: string;
  /** The campaign being edited: its trigger data is offered for personalization and previews. */
  campaignId?: string;
  /** Transactional (trading) pushes are checked and hinted with the recommended send settings. */
  category: 'marketing' | 'transactional';
  /** «separate»: a content per platform; «shared»: one content written for both platforms into both channels. */
  mode: PushComposerMode;
  /** Lets the editor switch the mode (the contents are converted); without it the mode is fixed. */
  onModeChange?: (mode: PushComposerMode) => void;
  /** The push channels of the variant: `content.push_android` / `content.push_ios`. */
  value: PushComposerValue;
  /** Every edit, with both channels; a platform switched off leaves its channel out. */
  onChange: (next: PushComposerValue) => void;
  /** A launched revision: nothing can change. */
  readOnly?: boolean;
  /** Why it is read-only, when it is not a launched version (a Canvas the employee may only view). */
  readOnlyNotice?: string;
  /** The last failed save or publish: a 422 `translation_incomplete` is shown in the locale tabs. */
  saveError?: unknown;
  /** Edits not saved yet: flagged, and the browser asks before the page closes. */
  dirty?: boolean;
}

/**
 * The push composer (BB-73, BB-76): Compose (texts, overrides, actions, buttons, image, translations), Settings (send
 * settings and key-value pairs) and Test (the rendered payload for a user on both platforms), next to a system-look
 * preview with a device and notification state per platform. The content is checked while it is edited
 * (`POST …/push/validate`), and each issue shows at its field.
 */
export function PushComposer({ environmentId, campaignId, category, mode, onModeChange, value, onChange, readOnly = false, readOnlyNotice = 'This version is launched: the push cannot change.', saveError, dirty = false }: PushComposerProps) {
  const platforms = platformsOf(value);
  const locales = useEnvironmentLocales(environmentId);
  const registry = usePushRegistry(environmentId);
  const [surfaces, setSurfaces] = useState<PreviewSurfaces>(DEFAULT_SURFACES);
  const [editing, setEditing] = useState<PushPlatform>(platforms[0] ?? 'android');
  const current = platforms.includes(editing) ? editing : platforms[0]!;
  const shared = { ...sharedContent(value), platforms };
  const contentOf = (p: PushPlatform): PushContentInput => (mode === 'shared' ? shared : contentFor(value, 'separate', p));
  const targets = mode === 'shared' ? [{ platforms, content: shared }] : platforms.map((p) => ({ platforms: [p], content: contentOf(p) }));
  const validation = usePushValidation(environmentId, category, targets, !readOnly);

  const togglePlatform = (p: PushPlatform, on: boolean) => onChange(setPlatforms(value, mode, on ? [...platforms, p] : platforms.filter((x) => x !== p)));
  const common = { environmentId, campaignId, registry, readOnly };

  /** Separate mode: a tab per platform, each with its own content. */
  const perPlatform = (label: string, render: (p: PushPlatform) => ReactNode) =>
    mode === 'shared' ? (
      render(current)
    ) : platforms.length === 1 ? (
      render(platforms[0]!)
    ) : (
      <Tabs label={label} value={current} onValueChange={(v) => setEditing(v as PushPlatform)} items={platforms.map((p) => ({ value: p, label: PLATFORM_LABELS[p], content: render(p) }))} />
    );
  // Separate mode writes every shown platform, so a new message keeps both channels after the first edit.
  const write = (p: PushPlatform) => (next: PushContentInput) =>
    onChange(mode === 'shared' ? writeShared(next, platforms) : platforms.reduce<PushComposerValue>((out, q) => writeSeparate(out, q, q === p ? next : contentOf(q)), {}));
  const platformsFor = (p: PushPlatform) => (mode === 'shared' ? platforms : [p]);
  const issuesFor = (p: PushPlatform) => issueLookup(validation.result, mode === 'shared' ? undefined : [p]);

  return (
    <section className="push-composer" aria-label="Push composer">
      {readOnly && <ReadOnlyBanner>{readOnlyNotice}</ReadOnlyBanner>}
      <UnsavedChangesGuard isDirty={dirty && !readOnly} />
      <div className="push-composer__head">
        {onModeChange && (
          <RadioGroup
            label="Content"
            options={[
              { value: 'separate', label: 'Separate content per platform', disabled: readOnly },
              { value: 'shared', label: 'Shared content for both platforms', disabled: readOnly },
            ]}
            value={mode}
            onValueChange={(next) => {
              if (next === mode) return;
              onChange(switchMode(value, next as PushComposerMode));
              onModeChange(next as PushComposerMode);
            }}
          />
        )}
        <fieldset className="push-composer__platforms">
          <legend>Platforms</legend>
          {PLATFORMS.map((p) => (
            <Checkbox key={p} label={PLATFORM_LABELS[p]} checked={platforms.includes(p)} disabled={readOnly || (platforms.length === 1 && platforms.includes(p))} onCheckedChange={(on) => togglePlatform(p, on)} />
          ))}
        </fieldset>
        {dirty && !readOnly && <StatusPill tone="warning">Unsaved changes</StatusPill>}
      </div>
      <PushChecksSummary result={validation.result} pending={!readOnly && validation.pending} error={validation.error} onCheck={validation.checkNow} disabled={readOnly} />
      <div className="push-composer__body">
        <PushPreviewPane platforms={platforms} contentOf={contentOf} locales={locales} surfaces={surfaces} onSurfacesChange={setSurfaces} />
        <div className="push-composer__editor">
          <Tabs
            label="Push message"
            items={[
              {
                value: 'compose',
                label: 'Compose',
                content: perPlatform('Platform content', (p) => (
                  <PushContentTab {...common} value={contentOf(p)} onChange={write(p)} platforms={platformsFor(p)} mode={mode} locales={locales} issues={issuesFor(p)} surfaces={surfaces} saveError={saveError} />
                )),
              },
              {
                value: 'settings',
                label: 'Settings',
                content: perPlatform('Platform settings', (p) => <PushSettingsTab {...common} category={category} value={contentOf(p)} onChange={write(p)} platforms={platformsFor(p)} issues={issuesFor(p)} />),
              },
              {
                value: 'test',
                label: 'Test',
                content: <PushTestTab environmentId={environmentId} campaignId={campaignId} contents={mode === 'shared' ? [shared] : platforms.map(contentOf)} />,
              },
            ]}
          />
        </div>
      </div>
    </section>
  );
}
